import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { IonCol, IonContent, IonFooter, IonRow, IonSplitPane } from '@ionic/react'
import ExamsPicklistContent from '../../examsPicklist/ExamsPicklist.content'
import ExamBarcomponent from '../../examBar/ExamBar.component'
import AssessmentFindingsComponent from '../../assessmentFindings/AssessmentFindings.component'
import AssessmentNarrativesComponent from '../../assessmentNarratives/AssessmentNarratives.component'
import { BodyAssessmentConstants } from '../../store/bodyAssessment.constants'
import BurnsRuleOfNinesContent from '../../burns/burnsRuleOfNines/content/BurnsRuleOfNines.content'
import BurnsTbsaContent from '../../burns/burnsTbsa/content/BurnsTbsa.content'
import DrainsTubesContent from '../../drainsTubes/content/DrainsTubes.content'
import ObstetricsContent from '../../obstetrics/content/Obstetrics.content'
import ActionButtons from '../../../../../components/actionButtons/ActionButtons.component'
import { showAlert } from '../../../../../components/alert/store/alert.actions'
import useDeviceMetrics from '../../../../../utilities/useDeviceMetrics'
import BodySurveyContent from '../../bodySurvey/content/BodySurvey.content'
import {
  resetBodySurveyValues,
  saveBodySurvey,
  setDefaults as setAssessmentDefaults,
  setDefaults,
} from '../../bodySurvey/store/bodySurvey.actions'
import { showToast } from '../../../../../components/toast/store/toast.actions'
import { ToastColor } from '../../../../../components/toast/store/toast.constants'
import { IOptions } from '../../../../../common/common.types'
import { checkSelectedBodyPartsHasSameComments, examMapping } from '../../../../../utilities/bodySurvey.util'
import AdditionalBurns from '../../burns/additionalBurns/AdditionalBurns.content'
import BurnsSummary from '../../burns/burnsTbsa/content/BurnsSummary.content'
import { getBodySurveyOptionsSelector } from '../../../../options/selectors'
import { TabConstants } from '../../../../../components/tabs/Tabs.constants'
import { useHistory } from 'react-router'
import { getBodyAssessmentSelector } from '../../selectors'
import { getBodySurveySelector } from '../../bodySurvey/selectors'
import './BodyAssessmentTablet.content.css'
import { getObstetricsSelector } from '../../obstetrics/selectors'

const SectionStates = {
  FINDINGS: 'findings',
  COMMENTS: 'comments',
  EMPTY: 'empty',
  ADDITIONAL_BURNS_INFORMATION: 'additional_burns_information',
  DOCUMENT_FINDINGS: 'document_findings',
}

const BodyAssessmentTablet = () => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const history = useHistory()

  const obstetrics = useSelector(getObstetricsSelector)
  const { isPatientPregnantPNNV } = obstetrics

  const { isTabletInLandscape } = useDeviceMetrics()
  const { selectedExam, bodySelectionMode } = useSelector(getBodyAssessmentSelector)
  const bodySurvey = useSelector(getBodySurveySelector)

  const examOptions = useSelector(getBodySurveyOptionsSelector)

  const { assessmentDefaultOptions } = examOptions
  const [activeRightSection, setActiveRightSection] = useState(SectionStates.FINDINGS)

  const { __selectedBodyParts, seq, findings, __setDefaults, __documentFindings, examValues, examValuesPNNVs } =
    bodySurvey
  const enable = examValues.length || examValuesPNNVs.length || findings.length || __selectedBodyParts.length > 0
  const hideFooterButtonsForExams: Array<string> = [
    BodyAssessmentConstants.BURNS_RULE_OF_NINES,
    BodyAssessmentConstants.BURNS_TBSA,
    BodyAssessmentConstants.OBSTETRICS,
    BodyAssessmentConstants.DRAINS_TUBES,
  ]

  const getLocationData = () => {
    if (selectedExam === BodyAssessmentConstants.BODY_SURVEY) {
      switch (bodySelectionMode.id) {
        case 1:
          return __selectedBodyParts
        case 2:
          return examMapping[BodyAssessmentConstants.EXTERNAL_SKIN]
        case 3:
          return examMapping[BodyAssessmentConstants.GENERAL_EXTREMITIES]
        default:
          return examMapping[selectedExam]
      }
    } else {
      return examMapping[selectedExam]
    }
  }

  const hasAllSameComments = checkSelectedBodyPartsHasSameComments(__selectedBodyParts, findings)
  const locationData: IOptions = getLocationData()

  const filterFindingsBasedOnLocation = findings?.filter(
    (exam) => locationData && locationData.some((part) => part.id === exam.locationId)
  )
  const hideComments =
    selectedExam === BodyAssessmentConstants.BODY_SURVEY &&
    bodySelectionMode.id === 1 &&
    __selectedBodyParts.length > 1 &&
    !hasAllSameComments

  const checkForSpecificExam = (
    [
      BodyAssessmentConstants.NEURO_CONDITIONS,
      BodyAssessmentConstants.MENTAL_CONDITIONS,
      BodyAssessmentConstants.CARDIO_CONDITIONS,
      BodyAssessmentConstants.OBSTETRICS,
      BodyAssessmentConstants.DRAINS_TUBES,
    ] as Array<string>
  ).includes(selectedExam)

  const hideBackBtn = checkForSpecificExam

  useEffect(() => {
    if (selectedExam === BodyAssessmentConstants.BODY_SURVEY) {
      if (
        ((__selectedBodyParts?.length > 0 && bodySelectionMode.id === 1) ||
          bodySelectionMode.id === 2 ||
          bodySelectionMode.id === 3) &&
        activeRightSection !== SectionStates.COMMENTS
      ) {
        setActiveRightSection(SectionStates.FINDINGS)
      } else if (__selectedBodyParts?.length === 0 && bodySelectionMode.id === 1) {
        setActiveRightSection(SectionStates.EMPTY)
      }
    } else if (checkForSpecificExam) {
      setActiveRightSection(SectionStates.COMMENTS)
    } else if (selectedExam === BodyAssessmentConstants.BURNS_TBSA) {
      setActiveRightSection(SectionStates.ADDITIONAL_BURNS_INFORMATION)
    } else if (selectedExam === BodyAssessmentConstants.BURNS_RULE_OF_NINES) {
      setActiveRightSection(SectionStates.DOCUMENT_FINDINGS)
    } else {
      setActiveRightSection(SectionStates.FINDINGS)
    }
  }, [selectedExam, bodySelectionMode, __selectedBodyParts])

  const handleCommentClick = () => {
    setActiveRightSection(SectionStates.COMMENTS)
  }

  const handleBackClick = () => {
    setActiveRightSection(SectionStates.FINDINGS)
  }

  const noFindingsSection = () => {
    return (
      <div id='body-assessment-empty-section' className='ion-text-center'>
        <h1>{t('bodyAssessment.BodySurvey')}</h1>
        <h4>{t('bodyAssessment.ExploreAndSelectBodyPartsToBegin')}</h4>
      </div>
    )
  }

  const examContent = () => {
    switch (selectedExam) {
      case BodyAssessmentConstants.BODY_SURVEY:
        if (__documentFindings) {
          return <AssessmentFindingsComponent bodySurvey={bodySurvey} />
        } else {
          return <BodySurveyContent />
        }
      case BodyAssessmentConstants.BURNS_RULE_OF_NINES:
        return <BurnsRuleOfNinesContent />
      case BodyAssessmentConstants.BURNS_TBSA:
        return <BurnsTbsaContent />
      case BodyAssessmentConstants.DRAINS_TUBES:
        return <DrainsTubesContent />
      case BodyAssessmentConstants.OBSTETRICS:
        return <ObstetricsContent />
      case BodyAssessmentConstants.NEURO_CONDITIONS:
      case BodyAssessmentConstants.MENTAL_CONDITIONS:
      case BodyAssessmentConstants.CARDIO_CONDITIONS:
        return <AssessmentFindingsComponent bodySurvey={bodySurvey} showCommentsIcon={!isTabletInLandscape} />
      default:
        return null
    }
  }

  const handleDelete = () => {
    dispatch(resetBodySurveyValues())
    dispatch(saveBodySurvey())
  }

  const handleConfirmSave = () => {
    dispatch(saveBodySurvey())
    history.push(TabConstants.ADDITIONAL_ASSESSMENTS)
  }

  const onDoneClick = () => {
    const alertState = {
      header: t('additionalAssessments.completeAssessment.header'),
      message: `${t('additionalAssessments.completeAssessment.message')} Assessment ${seq}`,
      buttons: [{ text: 'Cancel' }, { text: 'Ok', handler: handleConfirmSave }],
    }
    dispatch(showAlert(alertState))
  }

  const onClearClick = () => {
    const alertState = {
      header: t('additionalAssessments.clearBodySurvey.header'),
      message: `${t('additionalAssessments.clearBodySurvey.message')} ${seq}?`,
      buttons: [{ text: 'Cancel' }, { text: 'Ok', role: 'destructive', handler: handleDelete }],
    }
    dispatch(showAlert(alertState))
  }

  const handleDefaults = () => {
    if (assessmentDefaultOptions.length === 0) {
      dispatch(
        showToast({
          message: `${t('additionalAssessments.notifications.noDefaults')}`,
          position: 'top',
          color: ToastColor.Info,
        })
      )
      dispatch(setDefaults(false))
    } else {
      dispatch(setAssessmentDefaults())
      dispatch(setDefaults(true))
    }
  }

  const setDefaultsValue = () => {
    // handle assessment click
    const alertState = {
      header: t('additionalAssessments.setDefaults.header'),
      message: t('additionalAssessments.setDefaults.message'),
      buttons: [{ text: 'Cancel' }, { text: 'Ok', handler: handleDefaults }],
    }
    dispatch(showAlert(alertState))
  }

  const getFooterButtons = [
    {
      label: t('common.buttons.Clear'),
      className: 'btn mr-8',
      color: 'danger',
      fill: 'outline',
      onClick: () => onClearClick(),
      disabled: !enable,
      show: true,
    },
    {
      label: t('additionalAssessments.bodySurvey.defaults'),
      className: 'btn btn-primary',
      onClick: () => setDefaultsValue(),
      show: !__setDefaults,
    },
    {
      label: t('common.buttons.Done'),
      className: 'btn btn-primary',
      onClick: () => onDoneClick(),
      disabled: !enable,
      show: __setDefaults,
    },
  ]

  const filterFooterButtons = getFooterButtons.filter((footer) => footer.show)

  const hasPNValues =
    (selectedExam === BodyAssessmentConstants.OBSTETRICS &&
      isPatientPregnantPNNV &&
      [8801019, 8801023].includes(isPatientPregnantPNNV.id)) ||
    false

  return (
    <IonContent className='ion-padding'>
      <IonSplitPane content-id='body-assessment-tablet' id='body-assessment-tablet' className='no-scroll'>
        <div className='pt-10 body-assessment-sidebar'>
          <ExamsPicklistContent />
          {!hideFooterButtonsForExams.includes(selectedExam) && (
            <IonFooter className='sticky-footer'>
              <ActionButtons actionButtonObj={filterFooterButtons} />
            </IonFooter>
          )}
        </div>
        <IonContent id='body-assessment-tablet' className='body-assessment-tablet-content'>
          <IonRow className='ion-align-items-start h-100'>
            {isTabletInLandscape ? (
              <>
                <IonCol className='ion-no-padding h-100' size='6'>
                  <div className='body-assessment-middle-section h-100 ion-padding pt-0 pb-0'>
                    <ExamBarcomponent />
                    {examContent()}
                  </div>
                </IonCol>
                <IonCol className='ion-no-padding h-100' size='6'>
                  <div
                    className={`body-assessment-right-section h-100 ion-padding pb-0 ${
                      activeRightSection === SectionStates.EMPTY ? 'ion-flex ion-align-items-center' : ''
                    }`}>
                    {activeRightSection === SectionStates.EMPTY && noFindingsSection()}
                    {activeRightSection === SectionStates.FINDINGS && (
                      <AssessmentFindingsComponent bodySurvey={bodySurvey} handleCommentClick={handleCommentClick} />
                    )}
                    {activeRightSection === SectionStates.COMMENTS && (
                      <AssessmentNarrativesComponent
                        handleBackClick={handleBackClick}
                        hideBackBtn={hideBackBtn}
                        comments={filterFindingsBasedOnLocation[0]?.comments}
                        hideComments={hideComments}
                        disabled={hasPNValues}
                      />
                    )}
                    {activeRightSection === SectionStates.ADDITIONAL_BURNS_INFORMATION && <AdditionalBurns />}
                    {activeRightSection === SectionStates.DOCUMENT_FINDINGS && (
                      <>
                        <BurnsSummary />
                        <AdditionalBurns />
                      </>
                    )}
                  </div>
                </IonCol>
              </>
            ) : (
              <IonCol className='ion-no-padding h-100'>
                <div className='body-assessment-tablet-portrait-section ion-padding pt-0 pb-0 h-100'>
                  <ExamBarcomponent />
                  {examContent()}
                </div>
              </IonCol>
            )}
          </IonRow>
        </IonContent>
      </IonSplitPane>
    </IonContent>
  )
}

export default BodyAssessmentTablet
